import { Component, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SideNavService } from 'src/app/core-flc/sidenav/sidenav.service';
import { RouterLinkEnum } from 'src/app/enums/router-link-enum';
import { FileuploaderFilepondService } from 'src/app/file-uploader/fileuploader.filepond.service';
import { LayerSwitcherService } from 'src/app/map-openlayers/layerswitcher/layerswitcher.service';
import { RasterCatCode, RasterCategoryDto, RasterDto } from 'src/app/server-api/models';
import { RasterControllerService } from 'src/app/server-api/services';
import { AlertComponent } from 'src/app/utils-flc/alert/alert.component';
import { DetailComponent } from 'src/app/utils-flc/detail/detail.component';
import { LogMessagesService } from '../../log-messages/log-messages.service';
import { IRasterFormGroup } from './IRasterFormGroup';

@Component({
  selector: 'app-raster',
  templateUrl: './raster.component.html',
  styleUrls: ['./raster.component.css'],
  providers: [LayerSwitcherService]
})
export class RasterComponent extends DetailComponent {
  raster: RasterDto;
  colorMap: string;

  selectedCategory: RasterCategoryDto[] = [];
  category_disabled: string = '';
  rasterUpload: boolean;
  otherCode: RasterCatCode = RasterCatCode.Other;

  selectLogMessageOnMap: Subscription;

  @ViewChild('file', { static: true }) fileInput: ElementRef;
  fileToUpload: File;

  constructor(
    public uploaderService: FileuploaderFilepondService,
    public override translate: TranslateService,
    private rasterService: RasterControllerService,
    public override sidenavService: SideNavService,
    public override router: Router,
    private formBuilder: UntypedFormBuilder,
    public lsService: LayerSwitcherService,
    public logMessagesService: LogMessagesService
  ) {
    super(translate, sidenavService, router);
  }

  override ngOnInit()
  {
    this.profilesAllowEdit = 'ADMIN,ENGINEER,UPLOADER';
    super.ngOnInit();
    this.rasterUpload = this.routePath.indexOf(RouterLinkEnum.RASTERS_UPLOAD.link.split('/')[1]) !== -1;

    this.selectLogMessageOnMap = this.logMessagesService.toSelectLogMessagesOnMap.subscribe((id: string) => {
      this.lsService.selectLogMessage(id);
    });
  }


  override ngOnDestroy(): void {

    super.ngOnDestroy();
    if (this.selectLogMessageOnMap) this.selectLogMessageOnMap.unsubscribe();
  }

  override initItem(): void {
    super.initItem();
    //init an empty dto and set the invisible fields
    this.raster = {} as any as RasterDto;
    this.selectedCategory = [];

    this.initForm();
    this.columnsVisible.set('id', false);
  }

  override getItem(request) {
    super.getItem(request);

    this.rasterService.getRaster(request).subscribe((data: RasterDto) => {
      this.raster = data;
      this.colorMap = this.raster.category.colorMap;
      this.category_disabled = this.raster.category.name;

      this.name = data.originalFileName;
      this.selectedCategory[0] = this.raster.category;

      this.rasterUpload = this.raster.temporary;

      this.initForm();

      if (!this.raster.temporary)
      {
        this.columnsVisible.set('originalFileName', false);
        this.columnsVisible.set('created', false);
        this.columnsVisible.set('category', false);
        this.columnsVisible.set('category_disabled', true);
        this.columnsVisible.set('modelCalculation', true)
      }
    },
    (err: string) => {
      window.alert(err.replace('Object', 'Raster'));
    }
    );

  }

  override setForm() {
    //set visibility fields, set FormGroup and validators
    super.setForm();

    this.columnsVisible = new Map([
      ['id', false],
      ['originalFileName', true],
      ['created', true],
      ['lastModificationDate', true],
      ['category', true],
      ['category_disabled', false],
      ['temporary', false],
      ['modelCalculation', false]
    ]);

    if (this.id !== 'new')
    {
      this.detailForm = this.formBuilder.group({
        id: [''],
        originalFileName: [{value:'', disabled: true}, Validators.required],
        created: [{value:'', disabled: true}],
        lastModificationDate: [{value:'', disabled: true}],
        category: [{ value: '', disabled: this.disableByProfile}, Validators.required],
        temporary: [{value:false, disabled: true},],
        modelCalculation: [{value: false, disabled: this.disableByProfile}]

      }) as IRasterFormGroup;
    }
    else
    {
      this.detailForm = this.formBuilder.group({
        id: [''],
        originalFileName: [''],
        created: [""],
        lastModificationDate: [""],
        category: [[]],
        temporary: [true],
        modelCalculation: [true]

      }) as IRasterFormGroup;
    }

    this.detailForm.patchValue(this.raster);

    this.lsService.toAddRasterTiffLayer(this.id);
    this.lsService.toAddSectionLayer(null);
  }

  override initForm() {
    super.initForm();
    this.initDialogDelete('raster.dialog_title');

    //set topmenu icon actions
    this.translate.get(['icon_refresh', 'icon_menu', 'icon_next', 'icon_previous']).subscribe((translations: string) => {
      this.topMenuIcons = [
        {
          icon: 'refresh',
          title: translations['icon_refresh'],
          routerLink: null,
          clickMethod: 'refresh',
          hide: false
        },
        {
          icon: 'arrow_back',
          title: translations['icon_previous'],
          routerLink: null,
          clickMethod: 'navigatePrevious',
          hide: this.hidePrevious
        },
        {
          icon: 'arrow_forward',
          title: translations['icon_next'],
          routerLink: null,
          clickMethod: 'navigateNext',
          hide: this.hideNext
        },
        {
          icon: 'menu',
          title: translations['icon_menu'],
          routerLink: this.rasterUpload ? RouterLinkEnum.RASTERS_UPLOAD_LIST.toString() : RouterLinkEnum.RASTERS_LIST.toString(),
          clickMethod: 'dummy',
          hide: false
        },
      ];
    });
    if (this.rasterUpload) this.lsService.toAddSectionLayer(null);
    this.setForm();
  }


  dragEnd(unit, { sizes }) {
    this.lsService.onResizeContainer();
  }

  override saveOk() {
    super.saveOk();

    //do it this way when you do not want to update all fields
    this.raster.category = this.selectedCategory[0];
    this.raster.modelCalculation = this.detailForm.controls['modelCalculation'].value;

    if (this.id === 'new')
    {
      var response =
      this.rasterService.uploadRaster({
        zColumn: 2,
        category: this.raster.category.id,
        body: {
          file: this.uploaderService.getLoadedBlob(),
        }
        })
        ;
    }
    else
    {
      var response =
        this.rasterService.updateRaster({
              id: this.id,
              body: this.raster,
            })
          ;
    }


    response.subscribe(
      (data) => {
        this.sidenavService.setLoading(false);
        if (this.id === 'new')
        {

          this.router.navigateByUrl((this.rasterUpload ? RouterLinkEnum.RASTERS_UPLOAD.toString() : RouterLinkEnum.RASTERS.toString()) + data.id);
        }
        else
        {
          this.refresh();
        }
      },
      (err) => {
        this.sidenavService.setLoading(false);
        window.alert(this.translate.instant('raster.update_failed'));
      }
      );
    }



  override delete() {
    super.delete();
    this.rasterService.deleteRaster({ id: this.id }).subscribe(
      (data) => {
        this.sidenavService.setLoading(false);
        this.router.navigateByUrl(this.rasterUpload ? RouterLinkEnum.RASTERS_UPLOAD_LIST.toString() : RouterLinkEnum.RASTERS_LIST.toString());
      },
      (error) => {
        this.sidenavService.setLoading(false);
        window.alert(this.translate.instant('raster.delete_failed'));
      }
    );
  }

  mergeRaster()
  {
    //use a confirmDialog box before delete
    let mergeConfirmText = this.translate.instant('raster.merge-confirm', { name:this.name });
    let mergeDialogTitleText = this.translate.instant('raster.merge-dialog-title');
    const mergeConfirmDialog = this.dialog.open(AlertComponent, {
      data: {message: mergeConfirmText, type: mergeDialogTitleText, buttonCancel: this.buttonCancel, buttonConfirm: this.buttonConfirm }, hasBackdrop: true }

    );
    mergeConfirmDialog.afterClosed().subscribe(result => {
      if (result === true) {
        this.merge();
      }
    });
  }

  private merge()
  {
    this.sidenavService.setLoading(true);
    this.rasterService.mergeRaster({ id: this.id }).subscribe(
      (data) => {
        this.sidenavService.setLoading(false);
        this.router.navigateByUrl(RouterLinkEnum.RASTERS_UPLOAD_LIST.toString());
      },
      (error) => {
        this.sidenavService.setLoading(false);
        window.alert(this.translate.instant('raster.merge_failed'));
      }
    );
  }
  //https://www.javachinna.com/angular-spring-boot-crud-app/
}



